<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import { usePostHog } from '@/composables/usePostHog';
import { useRouter } from 'vue-router';

@Component
export default class NavIsland extends Vue {
  isScrolled = false;
  private posthog = usePostHog().posthog;
  private router = useRouter();

  handleScroll() {
    this.isScrolled = window.scrollY > 20;
  }

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll(); // Initial check
  }

  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  get activeSection(): 'home' | 'projects' | 'infra' | null {
    const route = this.$route;
    if (route.name === 'home' && !route.hash) {
      return 'home';
    }
    if (route.path.startsWith('/infrastructure')) {
      return 'infra';
    }
    // List of project routes or path pattern
    const projectRoutes = ['pikuTrade', 'cgj', 'referendum', 'sproogy', 'ndi', 'portfolio', 'projects'];
    if (projectRoutes.includes(route.name as string)) {
      return 'projects';
    }
    return null;
  }

  navigateTo(target: 'home' | 'projects' | 'infra') {
    this.posthog.capture('nav_island_clicked', { target: target });
    if (target === 'home') {
      this.router.push({ name: 'home' });
    } else if (target === 'infra') {
      this.router.push({ name: 'infrastructure' });
    } else {
      this.router.push({ name: 'projects' });
    }
  }
}
</script>

<template>
  <div class="nav-island-container">
    <nav :class="['nav-island', 'ln-glass-elevated', { 'is-scrolled': isScrolled }]">
      <button 
        :class="['island-item', { 'active': activeSection === 'home' }]"
        @click="navigateTo('home')"
      >
        <span class="mdi mdi-home-outline"></span>
        <span class="label">{{ $t('nav.home') }}</span>
      </button>

      <div class="island-separator"></div>

      <button 
        :class="['island-item', { 'active': activeSection === 'infra' }]"
        @click="navigateTo('infra')"
      >
        <span class="mdi mdi-server-network"></span>
        <span class="label">{{ $t('nav.infrastructure') }}</span>
      </button>
      
      <div class="island-separator"></div>
      
      <button 
        :class="['island-item', { 'active': activeSection === 'projects' }]"
        @click="navigateTo('projects')"
      >
        <span class="mdi mdi-rocket-launch-outline"></span>
        <span class="label">{{ $t('nav.projects') }}</span>
      </button>
    </nav>
  </div>
</template>

<style scoped>
.nav-island-container {
  position: fixed;
  top: 1.5rem;
  left: calc(50% + 40px);
  transform: translateX(-50%);
  z-index: 1002;
  pointer-events: none;
}

.nav-island {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  gap: 0.5rem;
  border-radius: 100px;
  pointer-events: auto;
  border: 1px solid var(--ln-glass-border-strong);
  box-shadow: 0 10px 30px var(--ln-glass-shadow-elevated);
  
  /* Visible by default */
  opacity: 1;
  transform: translateY(0) scale(1);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
  filter: blur(0);
}

.nav-island.is-scrolled {
  /* Ghost mode when scrolled */
  opacity: 0.25;
  transform: translateY(-5px) scale(0.95);
  filter: blur(2px);
}

@media (hover: hover) {
  .nav-island:hover {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
    box-shadow: 0 15px 40px var(--ln-glass-shadow-elevated);
  }
}

.island-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1.2rem;
  border-radius: 100px;
  border: none;
  background: transparent;
  color: var(--ln-text-secondary);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  white-space: nowrap;
}

.island-item span.mdi {
  font-size: 1.2rem;
  transition: transform var(--ln-transition-normal) var(--ln-ease-bounce);
}

@media (hover: hover) {
  .island-item:hover {
    background: var(--ln-glass-surface);
    color: var(--ln-teal-deep);
  }

  .island-item:hover span.mdi {
    transform: scale(1.2);
  }
}

.island-item.active {
  background: var(--ln-teal-deep);
  color: white;
  box-shadow: 0 4px 15px rgba(44, 122, 123, 0.3);
}

.island-separator {
  width: 1px;
  height: 20px;
  background: var(--ln-glass-border-strong);
  opacity: 0.5;
}

/* Tooltip style label for mobile or small screens */
@media (max-width: 768px) {
  .nav-island-container {
    left: 50%;
  }
  
  .label {
    display: none;
  }
  .island-item {
    padding: 0.6rem;
  }
  .nav-island {
    gap: 0.2rem;
  }
}
</style>
